import { useT } from '../../../i18n/I18nProvider';

/**
 * Which channel a conversation is on, read at a glance.
 *
 * Three decisions worth keeping.
 *
 * **Colour AND shape, never colour alone.** WhatsApp green, Messenger blue and
 * Instagram magenta are the marks people already know, but roughly one man in
 * twelve cannot tell the first two apart, and the whole point of this is being
 * readable without effort. So each mark carries its own glyph, and each carries
 * a name for a screen reader.
 *
 * **On every row, including WhatsApp.** An account with one channel gets a mark
 * that tells them nothing, which costs 14 pixels. An account with three gets a
 * queue they can scan. The alternative (hide it when only one channel is in
 * use) makes the mark's meaning depend on state the row cannot see, so the day
 * a second channel is connected every row changes shape at once.
 *
 * **A mark, not a badge.** It sits on the avatar's corner rather than in the
 * row's badge strip, because the badges are about the conversation (unread,
 * SLA, state) and this is about the person: where they are writing from.
 */
export function ChannelMark({
  channel,
  size = 14,
  className
}: {
  channel?: string | null;
  size?: number;
  className?: string;
}) {
  const t = useT();
  const key = channel === 'messenger' || channel === 'instagram' || channel === 'voice' || channel === 'web' || channel === 'email' ? channel : 'whatsapp';
  const label = t(`inbox.channel.${key}`);

  return (
    <span
      className={`rr-channel-mark rr-channel-mark--${key}${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size }}
      title={label}
    >
      <svg viewBox="0 0 16 16" width={size * 0.66} height={size * 0.66} aria-hidden focusable="false">
        {key === 'whatsapp' && (
          <path
            fill="currentColor"
            d="M8 1.5a6.4 6.4 0 0 0-5.5 9.7L1.6 14.5l3.4-.9A6.4 6.4 0 1 0 8 1.5Zm3.6 9.1c-.15.42-.88.8-1.22.83-.31.03-.7.04-1.13-.07a10.3 10.3 0 0 1-1.02-.38c-1.8-.78-2.97-2.6-3.06-2.72-.09-.12-.73-.97-.73-1.85s.46-1.31.63-1.49a.66.66 0 0 1 .48-.22h.34c.11 0 .26-.04.4.31l.55 1.33c.05.09.08.2.02.32l-.17.27-.25.27c-.08.08-.17.17-.07.33.1.17.44.72.94 1.17.64.57 1.18.75 1.35.83.17.09.27.08.37-.05l.53-.61c.12-.17.25-.14.42-.08l1.19.56c.17.09.29.13.33.2.04.08.04.44-.11.86Z"
          />
        )}
        {key === 'messenger' && (
          <path
            fill="currentColor"
            d="M8 1.3C4.3 1.3 1.4 4 1.4 7.7c0 2.1.94 3.96 2.4 5.17v2.53l2.2-1.2c.59.16 1.2.25 1.85.25 3.7 0 6.6-2.7 6.6-6.4S11.7 1.3 8 1.3Zm.68 8.6L6.98 8.1 3.7 9.9l3.6-3.83 1.73 1.8 3.24-1.8-3.6 3.83Z"
          />
        )}
        {key === 'voice' && (
          // A telephone handset: a phone call, read-only in the inbox (2 October 2026).
          <path
            fill="currentColor"
            d="M4.6 1.8c.4-.1.8.1 1 .5l1.1 2.4c.2.4.1.8-.2 1.1l-.9.9c.6 1.3 1.6 2.3 2.9 2.9l.9-.9c.3-.3.7-.4 1.1-.2l2.4 1.1c.4.2.6.6.5 1l-.4 1.8c-.1.4-.5.7-.9.7C6.6 13.1 2.9 9.4 2.9 3.6c0-.4.3-.8.7-.9l1-.9Z"
          />
        )}
        {key === 'web' && (
          // A globe: a chat from the website's chat bubble (3 October 2026).
          <>
            <circle cx="8" cy="8" r="5.8" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M2.4 8h11.2M8 2.2c1.7 1.6 2.5 3.6 2.5 5.8S9.7 12.2 8 13.8M8 2.2C6.3 3.8 5.5 5.8 5.5 8s.8 4.2 2.5 5.8" fill="none" stroke="currentColor" strokeWidth="1.2" />
          </>
        )}
        {key === 'email' && (
          // An envelope: an email thread (4 October 2026).
          <>
            <rect x="2" y="3.6" width="12" height="8.8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="m2.6 4.4 5.4 4.2 5.4-4.2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
          </>
        )}
        {key === 'instagram' && (
          <>
            <rect x="2.2" y="2.2" width="11.6" height="11.6" rx="3.6" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="8" cy="8" r="2.7" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="11.4" cy="4.6" r="0.95" fill="currentColor" />
          </>
        )}
      </svg>
      <span className="rr-visually-hidden">{label}</span>
    </span>
  );
}

/** The channel's name, for a heading or a filter. */
export function useChannelName(): (channel?: string | null) => string {
  const t = useT();
  return (channel) => {
    const key = channel === 'messenger' || channel === 'instagram' || channel === 'voice' || channel === 'web' || channel === 'email' ? channel : 'whatsapp';
    return t(`inbox.channel.${key}`);
  };
}
